/**
 * ExportExperience -- unified export choice dialog (plan sections 28-34).
 *
 * Collects scope / field / format choices, then calls onExport. The actual
 * transfer logic stays in use-table-export.ts and the generic server export endpoint.
 *
 * Scope mapping to existing onExport API:
 *   'all'      -> scope: 'all'       (server handles pagination)
 *   'filtered' -> scope: 'all'       (server honours active filters)
 *   'selected' -> scope: 'selection'
 */
import { useCallback, useMemo, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { TableDescriptor } from '@finance/contracts';
import { resolveTKey, translateRuntimeTKey } from '../../../i18n/resolve-key';
import { formatNumber } from '../../../utils/format';
import {
  getOrderedExportableColumns,
  getVisibleExportableColumns,
} from '../../experience/table/table-export-policy';
import type { TableState } from '../../experience/table/table-state';
import { TransferDialog } from '../shared/TransferDialog';
import { ExportRecordScope, type ExportScope } from './ExportRecordScope';
import { ExportFieldPicker, type ExportFieldGroup, type FieldSelectionMode } from './ExportFieldPicker';
import { ExportFormatPicker, type ExportFormat } from './ExportFormatPicker';

export interface ExportExperienceProps {
  open: boolean;
  onClose: () => void;
  resourceLabel: string;
  contract: TableDescriptor;
  tableState: TableState;
  selectedCount?: number;
  filteredCount?: number;
  onExport: (options: {
    scope: 'page' | 'selection' | 'all';
    format: 'csv' | 'xlsx';
    fields: string[];
  }) => void;
}

const EMPTY_STATE: TableState = {
  sorting: [], filters: [], globalSearch: '', grouping: [], aggregations: [],
  pagination: { page: 1, pageSize: 25 }, columnVisibility: [], columnOrder: [],
  columnSizes: [], pinnedColumns: { left: [], right: [] }, density: 'standard',
  expandedGroups: [], selection: { selectedIds: [], selectAllMode: 'page' },
  savedViewId: null, hasUnsavedChanges: false, showFilterRow: false,
} as unknown as TableState;

function buildFieldGroups(
  contract: TableDescriptor,
  t: (key: string) => string,
): ExportFieldGroup[] {
  const exportable = getOrderedExportableColumns(contract, EMPTY_STATE);
  return [{
    groupId: 'all',
    label: '',
    fields: exportable.map((col) => ({
      fieldId: col.field,
      label: translateRuntimeTKey(t, col.exportConfig?.exportLabelKey ?? col.labelKey) ?? col.field,
      recommended: col.defaultVisible !== false,
    })),
  }];
}

function defaultRecommendedFields(contract: TableDescriptor): string[] {
  return contract.columns
    .filter(
      (col) =>
        col.defaultVisible !== false &&
        col.exportConfig?.exportable !== false &&
        col.exportConfig?.redactionPolicy !== 'omit',
    )
    .map((col) => col.field);
}

export function ExportExperience({
  open,
  onClose,
  resourceLabel,
  contract,
  tableState,
  selectedCount = 0,
  filteredCount,
  onExport,
}: ExportExperienceProps) {
  const { t } = useTranslation();

  const transfer = contract.transfer?.export;
  const availableScopes: ExportScope[] = useMemo(
    () =>
      (transfer?.scopes ?? ['all', 'filtered', 'selected']).filter(
        (s): s is ExportScope => ['all', 'filtered', 'selected'].includes(s),
      ),
    [transfer],
  );
  const availableFormats: ExportFormat[] = useMemo(
    () =>
      (transfer?.formats ?? ['xlsx', 'csv']).filter(
        (f): f is ExportFormat => ['csv', 'xlsx'].includes(f),
      ),
    [transfer],
  );

  const visibleFields = useMemo(
    () => getVisibleExportableColumns(contract, tableState).map((col) => col.field),
    [contract, tableState],
  );
  const recommendedFields = useMemo(() => defaultRecommendedFields(contract), [contract]);

  const [scope, setScope] = useState<ExportScope>(availableScopes[0] ?? 'all');
  const [fieldMode, setFieldMode] = useState<FieldSelectionMode>('current');
  const [customFields, setCustomFields] = useState<string[]>(() => visibleFields);
  const [format, setFormat] = useState<ExportFormat>(availableFormats[0] ?? 'csv');

  const fieldGroups = useMemo(() => buildFieldGroups(contract, t), [contract, t]);

  const resolvedFields = useMemo(() => {
    if (fieldMode === 'recommended') return recommendedFields;
    if (fieldMode === 'current') return visibleFields;
    return customFields;
  }, [fieldMode, customFields, recommendedFields, visibleFields]);

  const handleSubmit = useCallback(() => {
    const exportScope: 'page' | 'selection' | 'all' =
      scope === 'selected' ? 'selection' : 'all';
    onExport({ scope: exportScope, format, fields: resolvedFields });
    onClose();
  }, [scope, format, resolvedFields, onExport, onClose]);

  return (
    <TransferDialog
      open={open}
      onClose={onClose}
      title={t(resolveTKey('tableExport.title'), { resource: resourceLabel })}
      description={t(resolveTKey('tableExport.description'))}
      data-testid="export-experience-dialog"
      footer={
        <>
          <Button onClick={onClose} color="inherit">
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={resolvedFields.length === 0}
            onClick={handleSubmit}
            data-testid="export-experience-submit"
          >
            {t(resolveTKey('tableExport.action'))}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <ExportRecordScope
          value={scope}
          onChange={setScope}
          filteredCount={filteredCount}
          selectedCount={selectedCount}
          availableScopes={availableScopes}
        />

        <Divider />

        <ExportFieldPicker
          mode={fieldMode}
          onModeChange={setFieldMode}
          selectedFields={customFields}
          onFieldsChange={setCustomFields}
          fieldGroups={fieldGroups}
        />

        <Divider />

        <ExportFormatPicker
          value={format}
          onChange={setFormat}
          availableFormats={availableFormats}
        />

        {(scope === 'all' || scope === 'filtered') && (transfer?.asyncThreshold ?? 0) > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t(resolveTKey('tableExport.asyncHint'), {
              threshold: formatNumber(transfer?.asyncThreshold ?? 0, 0),
            })}
          </Typography>
        )}
      </Box>
    </TransferDialog>
  );
}
